import { keepPreviousData, useQueries, useQuery } from '@tanstack/react-query';
import { useMemo } from 'react';
import { ChatType } from '../../../shared/types/monitor/ChatType';
import { domainsPerModelOptions } from '../../../shared/helpers/MonitorTypeHelpers';
import { getDomainsStats } from '../../../shared/services/monitor/data/sources/MonitorDataSourcesService';
import { MonitorType } from '../../../shared/types/monitor/MonitorType';

export interface ModelDomainsData {
  chatType: ChatType;
  name: string;
  icon: string;
  totalCount: number;
  domains: { domain: string; count: number }[];
}

interface UseDomainsPerModelParams {
  projectId: number;
  startDate: Date | null;
  endDate: Date | null;
  features: MonitorType[];
  selectedChatType: ChatType | null;
  monitorPromptIds?: number[];
  selectedChatTypes?: ChatType[];
  filterMonitorTypes?: string[];
  /** Domains picked in the filter: the per-model queries are narrowed to them instead of the top 10. */
  selectedDomains?: string[];
}

interface UseDomainsPerModelResult {
  modelsData: ModelDomainsData[];
  allDomains: string[];
  isLoading: boolean;
}

export const useDomainsPerModel = ({
  projectId,
  startDate,
  endDate,
  features,
  selectedChatType,
  monitorPromptIds,
  selectedChatTypes,
  filterMonitorTypes,
  selectedDomains,
}: UseDomainsPerModelParams): UseDomainsPerModelResult => {
  const enabledModels = useMemo(
    () => {
      const all = domainsPerModelOptions(features);
      if (selectedChatType && selectedChatType !== 'ALL') {
        return all.filter((opt) => opt.type === selectedChatType);
      }
      if (selectedChatTypes && selectedChatTypes.length > 0 && selectedChatTypes.length < all.length) {
        return all.filter((opt) => selectedChatTypes.includes(opt.type));
      }
      return all;
    },
    [features, selectedChatType, selectedChatTypes]
  );

  const fromTimestamp = startDate?.getTime() ?? null;
  const toTimestamp = endDate?.getTime() ?? null;
  const queryEnabled = !!projectId && !!startDate && !!endDate;
  // Without a selection each column is the model's top 10; with one, the backend returns only the
  // picked domains, so a domain outside the top 10 still gets its bar (the page size must fit them all).
  // Sorted: the same set picked in a different click order must hit the same cache entry.
  const domainFilter =
    selectedDomains && selectedDomains.length > 0 ? [...selectedDomains].sort() : undefined;
  const pageSize = Math.max(domainFilter?.length ?? 0, 10);

  const queries = useQueries({
    queries: enabledModels.map((model) => ({
      queryKey: [
        'domains-per-model-stats',
        projectId,
        model.type,
        fromTimestamp,
        toTimestamp,
        monitorPromptIds,
        domainFilter,
      ],
      queryFn: () =>
        getDomainsStats(projectId, false, model.type, startDate, endDate, 1, pageSize, domainFilter, undefined, monitorPromptIds),
      staleTime: 1000 * 60 * 5,
      refetchOnWindowFocus: false,
      refetchOnMount: false,
      enabled: queryEnabled,
      // Keep the previous bars while a new selection loads; `isLoading` then stays false, so the
      // skeleton shows only on the first load of the page.
      placeholderData: keepPreviousData,
    })),
  });

  const isLoading = queries.some((q) => q.isLoading);

  const modelsData = useMemo<ModelDomainsData[]>(() => {
    return enabledModels.map((model, index) => {
      const result = queries[index];
      const response = result?.data;
      const domains =
        response?.domains?.map((d) => ({ domain: d.domain, count: d.count })) ?? [];
      const totalCount = domains.reduce((sum, d) => sum + d.count, 0);

      return {
        chatType: model.type,
        name: model.name,
        icon: model.icon ?? '',
        totalCount,
        domains,
      };
    });
  }, [enabledModels, queries]);

  const allDomainsQuery = useQuery({
    queryKey: ['domains-per-model-all-domains', projectId, fromTimestamp, toTimestamp, monitorPromptIds, filterMonitorTypes],
    queryFn: () => getDomainsStats(projectId, false, 'ALL', startDate, endDate, 1, 100, undefined, undefined, monitorPromptIds, filterMonitorTypes),
    staleTime: 1000 * 60 * 5,
    refetchOnWindowFocus: false,
    refetchOnMount: false,
    enabled: queryEnabled,
  });

  const allDomains = useMemo<string[]>(() => {
    return allDomainsQuery.data?.domains?.map((d) => d.domain) ?? [];
  }, [allDomainsQuery.data]);

  return { modelsData, allDomains, isLoading };
};
